import { ViewportScroller } from '@angular/common';
import { Injectable, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class RouterScrollSuppressorService {
  private suppressNext = false;

  suppressNextRouterScroll(): void {
    this.suppressNext = true;
  }

  clearSuppression(): void {
    this.suppressNext = false;
  }

  consumeSuppression(): boolean {
    const suppressed = this.suppressNext;
    this.suppressNext = false;
    return suppressed;
  }
}

/**
 * Drop-in replacement for the default ViewportScroller that lets a navigation
 * opt out of the router's scroll-to-top (scrollPositionRestoration) via
 * RouterScrollSuppressorService.
 */
@Injectable()
export class SuppressibleViewportScroller extends ViewportScroller {
  private readonly suppressor = inject(RouterScrollSuppressorService);

  private offset: () => [number, number] = () => [0, 0];

  setOffset(offset: [number, number] | (() => [number, number])): void {
    this.offset = Array.isArray(offset) ? () => offset : offset;
  }

  getScrollPosition(): [number, number] {
    return [window.scrollX, window.scrollY];
  }

  scrollToPosition(position: [number, number]): void {
    if (this.suppressor.consumeSuppression()) {
      return;
    }
    window.scrollTo(position[0], position[1]);
  }

  scrollToAnchor(anchor: string): void {
    const element = document.getElementById(anchor) ?? document.getElementsByName(anchor)[0];
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const [offsetX, offsetY] = this.offset();
    window.scrollTo(window.scrollX + rect.left - offsetX, window.scrollY + rect.top - offsetY);
    element.focus();
  }

  setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
    window.history.scrollRestoration = scrollRestoration;
  }
}
